import { useEffect, useMemo } from 'react';
import { useCurrentUserApi, useClientApi } from '@hero/api-hooks';
import {
  useOrderDraftStore,
  type PublishOrderLoadDraft,
} from '@hero/hooks-shared';
import { resolveLoadDefaults } from './useApplyClientOrderDefaultsManager';

/**
 * Scoped variant of {@link useApplyClientOrderDefaultsManager} for
 * surfaces that mount an `<OrderDraftProvider>` (agent confirmation
 * card, reorder modal, edit drawer). Seeds org/client preferred
 * vehicles and load defaults into the scoped draft — the same
 * merge rule the backend uses before falling back to matched Route
 * vehicles in `OrderFetcher.resolveVehiclesForCreate`.
 *
 * MUST be called inside `<OrderDraftProvider>`.
 */
export function useOrderDraftApplyClientDefaultsManager(): void {
  const { data: me } = useCurrentUserApi();
  const customerId = useOrderDraftStore(s => s.customer?.customerId);
  const isInternalShipment = useOrderDraftStore(s => s.isInternalShipment);
  const lastApplied = useOrderDraftStore(s => s.loadAppliedSource);
  const applyLoadDefaults = useOrderDraftStore(s => s.applyLoadDefaults);

  const { data: client, isLoading: isClientLoading } = useClientApi(
    !isInternalShipment && customerId ? customerId : undefined,
  );

  const creatorOrg = me?.org;

  const defaults = useMemo<Partial<PublishOrderLoadDraft> | null>(() => {
    if (!creatorOrg) return null;
    return resolveLoadDefaults({
      orgSettings: creatorOrg.settings,
      clientSettings: !isInternalShipment ? client?.settings : undefined,
    });
  }, [creatorOrg, client?.settings, isInternalShipment]);

  useEffect(() => {
    if (!defaults) return;
    const orgId = creatorOrg?._id?.toString();
    const clientIdStr = !isInternalShipment ? customerId : undefined;
    if (clientIdStr && isClientLoading) return;
    if (lastApplied?.orgId === orgId && lastApplied?.clientId === clientIdStr) {
      return;
    }
    applyLoadDefaults({
      defaults,
      source: { orgId, clientId: clientIdStr },
    });
  }, [
    defaults,
    creatorOrg?._id,
    customerId,
    isInternalShipment,
    isClientLoading,
    lastApplied?.orgId,
    lastApplied?.clientId,
    applyLoadDefaults,
  ]);
}
